import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTour } from "@reactour/tour";
import { FaStarHalfAlt } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { checkTourStatus, closeAppTour } from "../../redux/ReduxSlices/authSlice";

const BASE_DASHBOARD = [
    { selector: ".sidebarDashboardButtonTooltip", content: "In this tab you will be able to get overview of all tasks." },
    { selector: ".totalAssessments", content: "Here you will find count of total number of assessments sent." },
    { selector: ".completed", content: "Here you will find count of how many assessments have been completed." },
    { selector: ".incomplete", content: "Here you will find count of how many assessments are not attempted." },
    { selector: ".openPositions", content: "Here you will find count of open positions which you have created from open positions tab." },
    { selector: ".closedPositions", content: "Here you will find count of closed positions for which hiring is completed." },
];

const OWNER_DASHBOARD_EXTRA = [
    { selector: ".ownerSiteAdmin", content: "Here you will see list of site admins. Site admins can carry payments and all other operartions on your behalf. Click on add button to add site admin." },
    { selector: ".ownerSuperAdmin", content: "Here you will see list of site manager. Site manager can add reporting managers and send assessments to new hires and existing employees. Also site manager can view all the assessment results. Click on add button to add site admin." },
    { selector: ".ownerReportingManager", content: "Here you will see list of reporting managers. Reporting managers can send assessments to existing employees." },
];
const HR_DASHBOARD_EXTRA = [
    { selector: ".hrSuperAdmin", content: "Here you will see list of super admins." },
    { selector: ".hrSiteAdmin", content: "Here you will see list of site admins." },
    { selector: ".hrReportingManager", content: "Here you will see list of reporting managers. Reporting managers can send assessments to existing employees. Click on add button to add reporting managers." },
];
const CFO_DASHBOARD_EXTRA = [
    { selector: ".cfoSuperAdmin", content: "Here you will see list of super admins." },
    { selector: ".cfoSiteManager", content: "Here you will see list of site manager. Site manager can add reporting managers and send assessments to new hires and existing employees. Also site manager can view all the assessment results. Click on add button to add site admin." },
    { selector: ".cfoReportingManager", content: "Here you will see list of reporting managers. Reporting managers can send assessments to existing employees. Click on add button to add reporting managers." },
];
const MANAGER_DASHBOARD_EXTRA = [
    { selector: ".managerSuperAdmin", content: "Here you will see list of super admins." },
    { selector: ".managerSiteAdmin", content: "Here you will see list of site manager." },
    { selector: ".managerSiteManager", content: "Here you will see list of reporting managers." },
];

const BASE_ASSESSMENT = [
    { selector: ".welcome", content: "Welcome to selfsync. Lets take a tour of Selfsync." },
    { selector: ".sidebarAssessmentsButtonTooltip", content: "In this tab you will be able to send assessments.", class: "note editorial" },
    { selector: ".newHireCard", content: "Here you can send assessments for hiring new candidates. Select New Hires and click next and follow simple steps to send assessment." },
    { selector: ".existingEmployeeCard", content: "Here you can send assessments to existing employees. Select existing employee and click next to follow simple steps to send assessment." },
];
const MANAGER_ASSESSMENT = [
    { selector: ".welcome", content: "Welcome to selfsync. Lets take a tour of Selfsync." },
    { selector: ".sidebarAssessmentsButtonTooltip", content: "In this tab you will be able to send assessments." },
    { selector: ".existingEmployeeCard", content: "Here you can send assessments to existing employees. Select existing employee and click next to follow simple steps to send assessment." },
];

const BASE_REPORT = [
    { selector: ".sidebarResultButtonTooltip", content: "In this tab you will be able to see all assessment results." },
    { selector: ".ownReportButtonTooltip", content: "See all assessment details sent by you." },
    { selector: ".vendorReportButtonTooltip", content: "See all assessment details sent by vendors." },
];
const MANAGER_REPORT = [
    { selector: ".sidebarResultButtonTooltip", content: "In this tab you will be able to see all assessment results." },
    { selector: ".ownReportButtonTooltip", content: "See all assessment details sent by you." },
];

const BASE_ADD_ADMIN = [
    { selector: ".sidebarTeamManagementButtonTooltip", content: "In this tab you will be able to add team members." },
];
const OWNER_ADD_ADMIN_EXTRA = [
    { selector: ".addSiteAdmin", content: "Fill form here to add Site Admin" },
    { selector: ".siteAdminList", content: "Here you will find the list of site admins you can also edit or delete site admin." },
    { selector: ".addSiteManager", content: "Fill form here to add Site Manager" },
    { selector: ".siteManagerList", content: "Here you will find the list of site managers you can also edit or delete site manager." },
    { selector: ".addReportingManager", content: "Fill form here to add Reporting Manager" },
    { selector: ".reportingManagerList", content: "Here you will find the list of reporting managers you can also edit or delete reporting manager." },
];
const HR_ADD_ADMIN_EXTRA = [
    { selector: ".addReportingManager", content: "Fill form here to add Reporting Manager" },
    { selector: ".reportingManagerList", content: "Here you will find the list of reporting managers you can also edit or delete reporting manager." },
];
const CFO_ADD_ADMIN_EXTRA = [
    { selector: ".addSiteManager", content: "Fill form here to add Site Manager" },
    { selector: ".siteManagerList", content: "Here you will find the list of site managers you can also edit or delete site manager." },
    { selector: ".addReportingManager", content: "Fill form here to add Reporting Manager" },
    { selector: ".reportingManagerList", content: "Here you will find the list of reporting managers you can also edit or delete reporting manager." },
];

// Per-role route order (stable)
const ROUTE_ORDER_BY_ROLE = {
    owner: ["/assessments", "/report", "/", "/open-positions", "/add-admin", "/settings", "/get-free-credits", "/email-template", "/helpinfo", "/pricing"],
    cfo: ["/assessments", "/report", "/", "/open-positions", "/add-admin", "/settings", "/get-free-credits", "/email-template", "/helpinfo", "/pricing"],
    hr: ["/assessments", "/report", "/", "/open-positions", "/add-admin", "/settings", "/get-free-credits", "/email-template", "/helpinfo",],
    manager: ["/assessments", "/report", "/", "/settings", "/get-free-credits", "/email-template", "/helpinfo",],
    default: ["/assessments", "/report", "/"],
};

export default function TourManager({ onSkip = () => { }, onComplete = () => { } }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { setIsOpen, setSteps, setCurrentStep, steps, currentStep, isOpen } = useTour();
    const dispatch = useDispatch()

    // userType that can change after login
    const [userType, setUserType] = useState(() => localStorage.getItem("userType"));
    useEffect(() => {
        const onUserType = (e) => setUserType(e.detail);
        window.addEventListener("auth:userType", onUserType);
        return () => window.removeEventListener("auth:userType", onUserType);
    }, []);

    // skip-all flag (persisted)
    const [skipAll, setSkipAll] = useState(() => localStorage.getItem("tour:skipped") === "1");

    // final modal
    const [showComplete, setShowComplete] = useState(false);

    // ---- derive guides (same as your code) ----
    const dashboardGuide = useMemo(() => {
        switch (userType) {
            case "owner": return [...BASE_DASHBOARD, ...OWNER_DASHBOARD_EXTRA];
            case "hr": return [...BASE_DASHBOARD, ...HR_DASHBOARD_EXTRA];
            case "cfo": return [...BASE_DASHBOARD, ...CFO_DASHBOARD_EXTRA];
            case "manager": return [...BASE_DASHBOARD, ...MANAGER_DASHBOARD_EXTRA];
            default: return BASE_DASHBOARD;
        }
    }, [userType]);

    const assessmentGuide = useMemo(() => (
        userType === "manager" ? MANAGER_ASSESSMENT : BASE_ASSESSMENT
    ), [userType]);

    const reportGuide = useMemo(() => (
        userType === "manager" ? MANAGER_REPORT : BASE_REPORT
    ), [userType]);

    const addAdminGuide = useMemo(() => {
        switch (userType) {
            case "owner": return [...BASE_ADD_ADMIN, ...OWNER_ADD_ADMIN_EXTRA];
            case "hr": return [...BASE_ADD_ADMIN, ...HR_ADD_ADMIN_EXTRA];
            case "cfo": return [...BASE_ADD_ADMIN, ...CFO_ADD_ADMIN_EXTRA];
            default: return BASE_ADD_ADMIN;
        }
    }, [userType]);

    const stepsMap = useMemo(() => ({
        "/": dashboardGuide,
        "/assessments": assessmentGuide,
        "/report": reportGuide,
        "/open-positions": [
            { selector: ".sidebarCreatePositionButtonTooltip", content: "In this tab you will be able to create positions for new hires." },
            { selector: ".createPositions", content: "Fill the form and create positions for new hires." },
            { selector: ".createPositionsInfo", content: "For more information click here." },
            { selector: ".positionsList", content: "You can see list of positions created and also update position details or activate or deactivate position." },
        ],
        "/add-admin": addAdminGuide,
        "/settings": [
            { selector: ".sidebarProfileButtonTooltip", content: "In this tab you will be able to manage your profile settings." },
            { selector: ".editProfile", content: "Click here to edit profile settings." },
            { selector: ".viewPurchaseHistory", content: "View your purchase history here." },
            { selector: ".viewCreditHistory", content: "View your credit history here." },
            { selector: ".addNotifierEmail", content: "You can add emails here which will receive emails when anyone submits assessment." },
        ],
        "/get-free-credits": [
            { selector: ".sidebarFreeCreditButtonTooltip", content: "Check this to get free credits." },
        ],
        "/email-template": [
            { selector: ".sidebarTemplatesButtonTooltip", content: "In this tab you will be able to manage email templates which candidates ." },
            { selector: ".exitingTemplatesTab", content: "In this tab you will be able to see exiting email templates." },
            { selector: ".customTemplatesTab", content: "In this tab you will be able to add custom email template." },
            { selector: ".sidebarContactUsButtonTooltip", content: "If you want to reach us click here." },
        ],
        "/helpinfo": [
            { selector: ".sidebarDocumentationButtonTooltip", content: "In this tab you will be able to read documentaion." },
            { selector: ".sidebarCreditButtonTooltip", content: "You can view your avaliable credits here." },
        ],
        "/pricing": [
            { selector: ".sidebarPlansButtonTooltip", content: "In this tab you will be able to see subscription plans." },
            { selector: ".inrPlans", content: "See all plans in INR" },
            { selector: ".usdPlans", content: "See all plans in USD" },
        ],
    }), [dashboardGuide, assessmentGuide, reportGuide, addAdminGuide]);

    const routeOrder = useMemo(() => (
        ROUTE_ORDER_BY_ROLE[userType] ?? ROUTE_ORDER_BY_ROLE.default
    ), [userType]);


    const handleEndTour = async () => {
        try {
            const response = await dispatch(closeAppTour()).unwrap()
            if (response.success) {
                await dispatch(checkTourStatus())
            }
        } catch (error) {
            console.log("errr", error)
        }
    }

    const handleClose = async () => {
        await handleEndTour()
        setShowComplete(false)
    }

    // ---- Global Skip ----
    const handleSkipAll = async () => {
        // e.preventDefault()
        // localStorage.setItem("tour:skipped", "1");
        await handleEndTour()
        setSkipAll(true);
        setIsOpen(false);
        try { onSkip(); } catch { }
    };

    // ---- Add skip button to tour card ----
    useEffect(() => {
        if (isOpen && !skipAll) {
            // Inject custom styles for the skip button
            const style = document.createElement('style');
            style.textContent = `
        .tour-skip-button {
          position: absolute;
          top: -16px;
          right: -10px;
          padding: 2px 4px;
          border: 1px solid #BC99F7;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          color: #6d28d9;
          z-index: 100001;
          transition: all 0.2s ease;
          margin-bottom:10px;
        }
        .tour-skip-button:hover {
          background: #f9fafb;
          color: #6a1de3;
        }
        .reactour__popover {
          position: relative;
        }
          [data-tour-elem="content"] {
          margin-top: 40px !important;
        }
      `;
            document.head.appendChild(style);

            // Function to add skip button to tour popover
            const addSkipButton = () => {
                const popover = document.querySelector('.reactour__popover');
                if (popover && !popover.querySelector('.tour-skip-button')) {
                    const skipButton = document.createElement('button');
                    skipButton.className = 'tour-skip-button';
                    skipButton.textContent = 'Skip tour';
                    skipButton.onclick = handleSkipAll;
                    popover.appendChild(skipButton);
                }
            };

            // Add skip button immediately and set up observer for dynamic updates
            addSkipButton();

            const observer = new MutationObserver(addSkipButton);
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });

            // Also check periodically in case the popover is recreated
            const interval = setInterval(addSkipButton, 500);

            return () => {
                observer.disconnect();
                clearInterval(interval);
                document.head.removeChild(style);
                const skipButton = document.querySelector('.tour-skip-button');
                if (skipButton) {
                    skipButton.remove();
                }
            };
        }
    }, [isOpen, skipAll]);

    // ---- Build & open steps for the current route (respect skipAll) ----
    useEffect(() => {
        if (!userType || skipAll) return;

        const currPath = location.pathname;
        const stepsForThisRoute = stepsMap[currPath] || [];

        const idx = routeOrder.indexOf(currPath);
        const nextRoute = idx >= 0 ? routeOrder[idx + 1] : undefined;

        // handoff step (auto-advance between routes; final shows popup)
        const handoffStep = {
            selector: "body",
            popover: { position: "center" },
            meta: "handoff",
            content: nextRoute ? "Continuing to the next page…" : "Tour complete 🎉",
        };

        setSteps([...stepsForThisRoute, handoffStep]);
        setCurrentStep(0);
        const t = setTimeout(() => setIsOpen(true), 60);
        return () => clearTimeout(t);
    }, [userType, skipAll, location.pathname, stepsMap, routeOrder, setSteps, setIsOpen, setCurrentStep]);

    // ---- Auto-advance & final completion popup ----
    useEffect(() => {
        if (!userType || skipAll) return;
        if (!isOpen || !steps?.length) return;
        const s = steps[currentStep];
        if (s?.meta === "handoff") {
            const idx = routeOrder.indexOf(location.pathname);
            const nextRoute = idx >= 0 ? routeOrder[idx + 1] : undefined;

            if (nextRoute) {
                // go to next route
                setIsOpen(false);
                setTimeout(() => navigate(nextRoute), 50);
            } else {
                // LAST ROUTE -> show completion popup
                setIsOpen(false);
                setShowComplete(true);
                // optional callback for analytics or anything else
                try { onComplete(); } catch { }
            }
        }
    }, [isOpen, steps, currentStep, location.pathname, routeOrder, navigate, setIsOpen, onComplete]);

    // ---- UI: Completion modal ----
    return (
        <>
            {/* Completion modal */}
            {showComplete && (
                <div className="fixed inset-0 flex items-center justify-center backdrop-blur-sm bg-black bg-opacity-70 p-4 z-[100000]">
                    <div className="relative bg-gradient-to-br from-white to-violet-50 p-6 rounded-xl shadow-2xl w-[380px]">
                        <div className="text-center mb-4">
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">Tour complete 🎉</h3>
                            <p className="text-gray-600">
                                You've finished the guided tour.
                            </p>
                        </div>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={(e) => handleClose(e)}
                                className="bg-white border border-violet-300 text-violet-600 py-2 px-6 rounded-md hover:bg-violet-100 transition-all"
                            >
                                Close
                            </button>
                            {/* Uncomment if you want the restart tour button */}
                            {/* <button
                    onClick={() => {
                        setShowComplete(false);
                        localStorage.removeItem("tour:skipped");
                        navigate((ROUTE_ORDER_BY_ROLE[userType] ?? ROUTE_ORDER_BY_ROLE.default)[0] || "/");
                        setTimeout(() => setIsOpen(true), 120);
                    }}
                    className="btn-gradient text-white py-2 px-6 rounded-md transition-all"
                >
                    Restart tour
                </button> */}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}